import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatINR, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry", "Chandigarh",
  "Andaman and Nicobar Islands", "Dadra and Nagar Haveli and Daman and Diu", "Lakshadweep",
];
const COUNTRIES = ["India", "United States", "United Kingdom", "United Arab Emirates", "Australia", "Canada", "Singapore"];

const Req = () => <span className="text-[#7a1e2b] ml-0.5" aria-label="required">*</span>;

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [address, setAddress] = useState({
    full_name: user?.name || "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
  });
  const [payment, setPayment] = useState("razorpay");
  const [codEnabled, setCodEnabled] = useState(true);

  useEffect(() => {
    api.get("/store-config").then((r) => {
      const enabled = r.data?.cod_enabled !== false;
      setCodEnabled(enabled);
      if (!enabled) setPayment("razorpay");
    }).catch(() => {});
  }, []);

  const shipping = subtotal >= 2000 ? 0 : 99;
  const tax = Math.round(subtotal * 0.05);
  const total = subtotal + shipping + tax;

  const on = (k) => (e) => setAddress({ ...address, [k]: e.target.value });

  const openRazorpay = (order, keyId) => {
    const openIt = () => {
      // eslint-disable-next-line no-undef
      const rz = new window.Razorpay({
        key: keyId || "rzp_test_placeholder",
        amount: Math.round(order.total * 100),
        currency: "INR",
        name: "Sarees by Raunak",
        description: "Grace in every drape",
        order_id: order.razorpay_order_id,
        prefill: {
          name: address.full_name,
          email: user?.email || "",
          contact: address.phone,
        },
        theme: { color: "#7a1e2b" },
        handler: async (res) => {
          await api.post("/orders/verify", {
            order_id: order.order_id,
            razorpay_order_id: order.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          clear();
          navigate(`/order/${order.order_id}`);
        },
        modal: {
          ondismiss: async () => {
            // In demo mode, still complete the order
            if (order.demo_mode) {
              await api.post("/orders/verify", {
                order_id: order.order_id,
                razorpay_order_id: order.razorpay_order_id,
                razorpay_payment_id: "demo_pay_" + Date.now(),
                razorpay_signature: "demo",
              });
              clear();
              toast.success("Demo payment complete", { description: "Add real Razorpay keys to enable live checkout." });
              navigate(`/order/${order.order_id}`);
            }
          },
        },
      });
      rz.open();
    };
    if (window.Razorpay) return openIt();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = openIt;
    s.onerror = async () => {
      // Fallback: auto-confirm demo
      await api.post("/orders/verify", {
        order_id: order.order_id,
        razorpay_order_id: order.razorpay_order_id,
        razorpay_payment_id: "demo_pay_" + Date.now(),
        razorpay_signature: "demo",
      });
      clear();
      toast.success("Order placed (demo mode)");
      navigate(`/order/${order.order_id}`);
    };
    document.body.appendChild(s);
  };

  const placeOrder = async () => {
    if (!user) {
      toast.error("Please sign in to place your order");
      navigate("/login?redirect=/checkout");
      return;
    }
    if (items.length === 0) return toast.error("Your bag is empty");
    for (const k of ["full_name", "phone", "line1", "city", "state", "pincode"]) {
      if (!address[k]) return toast.error("Please fill all address fields");
    }
    setPlacing(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_id: i.variant_id || null })),
        address,
        payment_method: payment,
      };
      const { data } = await api.post("/orders", payload);
      if (payment === "razorpay") {
        // In demo mode (placeholder keys), skip the Razorpay SDK entirely
        if (data.order.demo_mode) {
          await api.post("/orders/verify", {
            order_id: data.order.order_id,
            razorpay_order_id: data.order.razorpay_order_id,
            razorpay_payment_id: "demo_pay_" + Date.now(),
            razorpay_signature: "demo",
          });
          clear();
          toast.success("Payment complete (demo mode)", { description: "Add real Razorpay keys to enable live checkout." });
          navigate(`/order/${data.order.order_id}`);
        } else {
          openRazorpay(data.order, data.razorpay_key_id);
        }
      } else {
        clear();
        toast.success("Order placed. We'll deliver soon!");
        navigate(`/order/${data.order.order_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12 min-h-[calc(100vh-200px)]" data-testid="checkout-page">
      <div className="mb-10">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a34a] mb-2">Checkout</div>
        <h1 className="font-serif text-4xl text-[#2b1b1c]">Almost yours.</h1>
        <p className="text-xs text-[#7c7273] mt-2">Fields marked <span className="text-[#7a1e2b]">*</span> are required</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-12">
        <div className="space-y-10">
          <section>
            <h2 className="font-serif text-2xl mb-6">Shipping Address</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="addr-name" className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Full name<Req /></Label>
                <Input id="addr-name" value={address.full_name} onChange={on("full_name")} className="rounded-none" data-testid="addr-name" required />
              </div>
              <div>
                <Label htmlFor="addr-phone" className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Phone<Req /></Label>
                <Input id="addr-phone" type="tel" value={address.phone} onChange={on("phone")} className="rounded-none" data-testid="addr-phone" required />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="addr-line1" className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Address line 1<Req /></Label>
                <Input id="addr-line1" value={address.line1} onChange={on("line1")} className="rounded-none" data-testid="addr-line1" required />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="addr-line2" className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Address line 2 <span className="text-[#7c7273] normal-case">(optional)</span></Label>
                <Input id="addr-line2" value={address.line2} onChange={on("line2")} className="rounded-none" data-testid="addr-line2" />
              </div>
              <div>
                <Label htmlFor="addr-city" className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">City<Req /></Label>
                <Input id="addr-city" value={address.city} onChange={on("city")} className="rounded-none" data-testid="addr-city" required />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">State<Req /></Label>
                {address.country === "India" ? (
                  <Select value={address.state} onValueChange={(v) => setAddress({ ...address, state: v })}>
                    <SelectTrigger className="rounded-none bg-white" data-testid="addr-state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={address.state} onChange={on("state")} className="rounded-none" data-testid="addr-state" placeholder="State / Province" required />
                )}
              </div>
              <div>
                <Label htmlFor="addr-pincode" className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Pincode<Req /></Label>
                <Input id="addr-pincode" inputMode="numeric" value={address.pincode} onChange={on("pincode")} className="rounded-none" data-testid="addr-pincode" required />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Country<Req /></Label>
                <Select value={address.country} onValueChange={(v) => setAddress({ ...address, country: v, state: v === "India" ? address.state : "" })}>
                  <SelectTrigger className="rounded-none bg-white" data-testid="addr-country">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-2xl mb-4">Payment</h2>
            <div className="space-y-3">
              <label className={`block border p-4 cursor-pointer transition-colors ${payment === "razorpay" ? "border-[#7a1e2b] bg-white" : "border-[#e8e4db]"}`} data-testid="pay-razorpay">
                <input type="radio" name="pay" value="razorpay" checked={payment === "razorpay"} onChange={() => setPayment("razorpay")} className="mr-3 accent-[#7a1e2b]" />
                <span className="font-medium">Razorpay</span>
                <span className="ml-2 text-sm text-[#7c7273]">UPI · Cards · Netbanking · Wallets</span>
              </label>
              {codEnabled && (
                <label className={`block border p-4 cursor-pointer transition-colors ${payment === "cod" ? "border-[#7a1e2b] bg-white" : "border-[#e8e4db]"}`} data-testid="pay-cod">
                  <input type="radio" name="pay" value="cod" checked={payment === "cod"} onChange={() => setPayment("cod")} className="mr-3 accent-[#7a1e2b]" />
                  <span className="font-medium">Cash on Delivery</span>
                  <span className="ml-2 text-sm text-[#7c7273]">Pay when it arrives at your door.</span>
                </label>
              )}
            </div>
          </section>
        </div>

        <aside className="bg-[#f5f3ef] p-8 lg:sticky lg:top-28 lg:self-start" data-testid="checkout-summary">
          <h3 className="font-serif text-xl mb-4">Order Summary</h3>
          <ul className="space-y-3 mb-4 max-h-64 overflow-y-auto pr-2">
            {items.map((it) => (
              <li key={`${it.product_id}::${it.variant_id || "base"}`} className="flex gap-3 text-sm">
                <img src={imgUrl(it.image)} alt={it.name} className="w-14 h-16 object-cover" />
                <div className="flex-1">
                  <div className="font-serif text-[#2b1b1c] leading-tight">{it.name}</div>
                  {it.color && <div className="text-[10px] text-[#7c7273] uppercase tracking-widest">{it.color}</div>}
                  <div className="text-[#7c7273]">Qty {it.quantity}</div>
                </div>
                <div>{formatINR(it.price * it.quantity)}</div>
              </li>
            ))}
          </ul>
          <div className="h-px bg-[#e8e4db] mb-4" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#7c7273]">Subtotal</span><span>{formatINR(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-[#7c7273]">Shipping</span><span>{shipping === 0 ? "Free" : formatINR(shipping)}</span></div>
            <div className="flex justify-between"><span className="text-[#7c7273]">Tax</span><span>{formatINR(tax)}</span></div>
            <div className="flex justify-between text-lg font-serif pt-2 border-t border-[#e8e4db]"><span>Total</span><span className="text-[#7a1e2b]">{formatINR(total)}</span></div>
          </div>
          <button className="btn-primary w-full mt-6 flex items-center justify-center gap-2" disabled={placing} onClick={placeOrder} data-testid="place-order-btn">
            <Lock size={14} /> {placing ? "Placing…" : `Place Order · ${formatINR(total)}`}
          </button>
          <p className="text-[11px] text-[#7c7273] text-center mt-3">Your details are encrypted and secure.</p>
        </aside>
      </div>
    </div>
  );
}
